import {
  ChakraFlex as Flex,
  ChakraBox as Box,
  ChakraText as Text,
  ChakraButton as Button,
} from "@airhelp/react-ui-components";
import config from "config/config";
import { useTranslations } from "contexts/LocalizationContext/hooks";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { trackUAEvent, trackEcommerceEvent } from "utils/tracking";
import PlanBoxItem from "./PlanBoxItem";

const PlanBox = ({ planDetails, gaEvent, uaEvent, mt, ...props }) => {
  const {
    planType,
    promoText,
    nameText,
    price,
    priceInfoText,
    translationVariables,
    items,
    buttonText,
    buttonType,
    buttonPath,
    pricePerMonth,
  } = planDetails;

  const [buttonHref, setButtonHref] = useState(`${config.ahplusUrl}${buttonPath}`);
  const translations = useTranslations();

  const handlertrackEvents = () => {
    trackUAEvent(uaEvent);
    trackEcommerceEvent(gaEvent);
  };

  useEffect(() => {
    const planHref = new URL(buttonHref);
    const urlParams = new URLSearchParams(window.location.search);
    const urlSource = urlParams.get("source");

    if (!urlSource) return;

    planHref.searchParams.set("channel", `${urlSource}_${planType}`);
    setButtonHref(planHref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex mx={{ md: 4 }} mt={mt} bg="white" w="full" maxW="400px" rounded="xl" direction="column" {...props}>
      {promoText && (
        <Box
          p={3}
          backgroundColor="success.500"
          color="white"
          textTransform="uppercase"
          fontWeight="semibold"
          roundedTop="xl"
          textAlign="center"
          dangerouslySetInnerHTML={{ __html: translations(promoText, translationVariables) }}
        />
      )}
      <Box flexGrow="1" px={{ base: 6, lg: 16 }}>
        <Box mt={8} mb={10} textAlign="center">
          <Text data-testid={`pricing-header-${planType}`} fontWeight="semibold" fontSize="4xl">
            {translations(nameText)}
          </Text>
          {price ? (
            <Text
              data-testid={`pricing-value-${planType}`}
              color="primary.500"
              fontWeight="semibold"
              fontSize="6xl"
              lineHeight="tall"
              mr={2}
            >
              <Box as="span" verticalAlign="text-top" fontSize="2xl" mr={2} color="greyscale.600">
                €
              </Box>
              {price}
              <Box as="span" verticalAlign="text-bottom" fontSize="2xl" ml="1" color="greyscale.600">
                /{translations("plan_per_year")}
              </Box>
            </Text>
          ) : (
            <Text
              data-testid={`pricing-value-${planType}`}
              color="primary.500"
              fontWeight="semibold"
              fontSize="4xl"
              lineHeight="tall"
              mt={3}
              mb={2}
            >
              {translations("plan_free")}
            </Text>
          )}
          <Text color="greyscale.600" fontSize="sm">
            {priceInfoText
              ? translations(priceInfoText)
              : `${translations("plan_per_passenger")}, ${translations("plan_billed_annually")}`}
          </Text>
        </Box>
        <Box mb={6}>
          {items.map((item) => (
            <PlanBoxItem key={item.text} item={item} />
          ))}
        </Box>
      </Box>
      <Box mb={12} px={{ base: 6, lg: 16 }} w="full">
        <Button
          variant={buttonType}
          data-testid={`pricing-choose-${planType}`}
          mt={6}
          mb={pricePerMonth ? 4 : 9}
          w="full"
          as="a"
          href={buttonHref}
          title={translations(buttonText)}
          onClick={handlertrackEvents}
        >
          {translations(buttonText)}
        </Button>
        {pricePerMonth && (
          <Text
            color="greyscale.600"
            textAlign="center"
            fontSize="sm"
            dangerouslySetInnerHTML={{
              __html: translations("plan_effectively_per_month", {
                "%price%": `<strong>${pricePerMonth}</strong>`,
              }),
            }}
          />
        )}
      </Box>
    </Flex>
  );
};

export default PlanBox;

PlanBox.propTypes = {
  planDetails: PropTypes.shape({
    planType: PropTypes.string,
    promoText: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    priceInfoText: PropTypes.string,
    pricePerMonth: PropTypes.string,
    translationVariables: PropTypes.PropTypes.shape({}),
    nameText: PropTypes.string,
    nameFontColor: PropTypes.string,
    buttonType: PropTypes.string,
    buttonText: PropTypes.string,
    buttonPath: PropTypes.string,
    bottomText: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        translationVariables: PropTypes.shape({}),
      })
    ),
  }).isRequired,
  gaEvent: PropTypes.PropTypes.shape({
    event: PropTypes.string,
    item_list_id: PropTypes.string,
    item_list_name: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        index: PropTypes.number,
        item_brand: PropTypes.string,
        item_category: PropTypes.string,
        item_category2: PropTypes.string,
        item_id: PropTypes.string,
        item_list_id: PropTypes.string,
        item_list_name: PropTypes.string,
        item_name: PropTypes.string,
        item_variant: PropTypes.string,
        price: PropTypes.number,
        quantity: PropTypes.number,
      })
    ),
  }),
  uaEvent: PropTypes.arrayOf(PropTypes.string),
  mt: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({
      base: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      sm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      md: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lg: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ]),
};

PlanBox.defaultProps = {
  gaEvent: {},
  mt: "",
};
